import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import * as workoutService from '../../services/workoutService';

const WEEK_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const TYPE_COLORS = { gym: '#8b5cf6', running: '#3b82f6', rest: '#6b7280' };
const TYPE_LABELS = { gym: 'Gym', running: 'Running', rest: 'Rest' };

// Each task can carry a `videoUrl` field. Supported: YouTube (watch, youtu.be, shorts, embed),
// Vimeo, and direct video files (.mp4, .webm, .mov). Any other URL (e.g. a normal web page, which sites
// usually block from being embedded) is shown as an "Open in new tab" link instead.
const getVideoUrl = (task) => task?.videoUrl?.trim() || '';

// Returns how the video should be rendered: { kind: 'iframe' | 'video' | 'link', src }
const getVideoSource = (url) => {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (youtube) return { kind: 'iframe', src: `https://www.youtube.com/embed/${youtube[1]}` };

  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}` };

  if (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url)) return { kind: 'video', src: url };

  return { kind: 'link', src: url };
};

const parseDate = (dateStr) => new Date(`${dateStr}T00:00:00`);

// Backend scheduledDate arrives as a full ISO timestamp (or a Date instance).
// Scheduled dates are date-only values stored at midnight UTC, so for strings we
// read the date part directly to avoid timezone shifts. Date instances use local time.
const toDateKey = (value) => {
  if (typeof value === 'string') return value.slice(0, 10);
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayKey = () => toDateKey(new Date());

// Monday-first week containing the given date key
const getWeekDates = (dateKey) => {
  const anchor = parseDate(dateKey);
  const dayOfWeek = anchor.getDay(); // 0=Sun ... 6=Sat
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() + diffToMonday);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return toDateKey(d);
  });
};

const addDays = (dateKey, days) => {
  const d = parseDate(dateKey);
  d.setDate(d.getDate() + days);
  return toDateKey(d);
};

const TODAY = getTodayKey();

const getOrdinalSuffix = (day) => {
  if (day % 10 === 1 && day !== 11) return 'st';
  if (day % 10 === 2 && day !== 12) return 'nd';
  if (day % 10 === 3 && day !== 13) return 'rd';
  return 'th';
};

const formatDayLabel = (dateStr) => {
  const date = parseDate(dateStr);
  const today = parseDate(TODAY);
  const diffDays = Math.round((date - today) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays === 1) return 'Tomorrow';

  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const day = date.getDate();
  return `${weekday} ${day}${getOrdinalSuffix(day)} ${month}`;
};

// Builds the target summary line, e.g. "3 x 12 · 40 kg" or "3 km · 20 min · 6:15-6:45 /km"
// Strength formats (3 x 12, 3 x 30m, 3 x 45s) are preserved.
// Weight is shown for gym sessions only; a weight of 0 is shown as "Bodyweight".
const formatTaskTarget = (task, sessionType) => {
  const parts = [];
  const unit = task.unit || 'km';

  if (task.targetSets && task.targetDistance) {
    parts.push(`${task.targetSets} x ${task.targetDistance}m`);
  } else if (task.targetSets && task.targetReps) {
    parts.push(`${task.targetSets} x ${task.targetReps}`);
  } else if (task.targetSets && task.targetDuration) {
    parts.push(`${task.targetSets} x ${task.targetDuration}s`);
  } else {
    if (task.targetDistance) parts.push(`${task.targetDistance} ${unit}`);
    if (task.targetDuration) parts.push(`${Math.round(task.targetDuration / 60)} min`);
  }

  if (sessionType === 'gym' && task.targetWeight != null) {
    parts.push(task.targetWeight === 0 ? 'Bodyweight' : `${task.targetWeight} ${task.weightUnit || 'kg'}`);
  }

  if (task.targetPace) parts.push(`${task.targetPace} /${unit}`);

  return parts.join(' · ');
};

const DailyTasksWidget = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const [activeSessionIndex, setActiveSessionIndex] = useState(0);
  const [videoTask, setVideoTask] = useState(null);

  // The visible week follows the selected date, so shifting the date by 7 days moves the strip
  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);
  const shiftWeek = (direction) => setSelectedDate((prev) => addDays(prev, direction * 7));

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await workoutService.getAll();
      setSessions(data.workouts || []);
    } catch (err) {
      setError(err.message || 'Failed to load workouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const getSessionsForDate = (date) => sessions.filter((s) => s.scheduledDate && toDateKey(s.scheduledDate) === date);
  const isDateCompleted = (date) => {
    const daySessions = getSessionsForDate(date);
    return daySessions.length > 0 && daySessions.every((s) => s.tasks.every((t) => t.completed));
  };

  const streakCount = weekDates.filter(isDateCompleted).length;
  const sessionsForSelectedDate = getSessionsForDate(selectedDate);
  const selectedSession = sessionsForSelectedDate[activeSessionIndex] || sessionsForSelectedDate[0];

  // Reset back to the first tab whenever a different day is selected
  useEffect(() => {
    setActiveSessionIndex(0);
  }, [selectedDate]);

  const resetToToday = () => setSelectedDate(TODAY);

  const toggleTask = async (sessionId, taskId, currentlyCompleted) => {
    const nextCompleted = !currentlyCompleted;

    // Optimistic update
    setSessions((prev) =>
      prev.map((session) =>
        session._id !== sessionId
          ? session
          : { ...session, tasks: session.tasks.map((t) => (t._id === taskId ? { ...t, completed: nextCompleted } : t)) }
      )
    );

    try {
      await workoutService.setTaskCompletion(sessionId, taskId, nextCompleted);
    } catch (err) {
      // Roll back on failure
      setSessions((prev) =>
        prev.map((session) =>
          session._id !== sessionId
            ? session
            : { ...session, tasks: session.tasks.map((t) => (t._id === taskId ? { ...t, completed: currentlyCompleted } : t)) }
        )
      );
      setError(err.message || 'Failed to update task');
    }
  };

  // Inline styles (kept in-file instead of a separate CSS file)
  const styles = {
    outer: { maxWidth: '100%', background: '#dfe7ee', borderRadius: 20, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    todayLabel: {
      background: 'none',
      border: 'none',
      padding: 0,
      fontWeight: 700,
      fontSize: '1.1rem',
      color: '#1f2937',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    streak: { fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: '0.25rem' },
    streakIcon: { color: '#f59e0b' },
    weekRow: { display: 'flex', alignItems: 'center', gap: '0.25rem' },
    weekStrip: { display: 'flex', justifyContent: 'space-between' },
    navButton: {
      background: 'none',
      border: 'none',
      padding: '0.25rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    dayCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', flex: 1 },
    dayLetter: { fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' },
    dayLetterToday: { color: '#111827' },
    tabRow: { display: 'flex', gap: '0.5rem' },
    tabButton: (active, color) => ({
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.4rem',
      padding: '0.5rem 0.75rem',
      borderRadius: 999,
      border: `1px solid ${active ? color : '#c9d2dc'}`,
      background: active ? color : '#ffffff',
      color: active ? '#ffffff' : '#374151',
      fontSize: '0.8rem',
      fontWeight: 700,
      cursor: 'pointer'
    }),
    tabDot: (color) => ({ width: 8, height: 8, borderRadius: '50%', background: color }),
    card: { background: '#ffffff', borderRadius: 16, padding: '1rem' },
    sessionTitle: { fontSize: '0.95rem', fontWeight: 700, color: '#111827', marginBottom: '0.25rem' },
    sessionMetaRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' },
    typeBadge: (color) => ({
      fontSize: '0.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      color: '#ffffff',
      background: color,
      borderRadius: 999,
      padding: '0.15rem 0.6rem'
    }),
    sessionNotes: { fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.75rem' },
    taskRow: { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.5rem 0', borderTop: '1px solid #f1f3f5' },
    taskName: { fontSize: '0.88rem', fontWeight: 600, color: '#111827' },
    taskNameDone: { textDecoration: 'line-through', color: '#9ca3af' },
    taskMeta: { fontSize: '0.78rem', color: '#6b7280' },
    taskTarget: { fontSize: '0.78rem', fontWeight: 600, color: '#374151' },
    dialogDescription: { fontSize: '0.9rem', lineHeight: 1.5, color: '#374151', marginTop: '0.75rem' },
    videoLink: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#3b82f6', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none' },
    videoButton: {
      background: 'none',
      border: 'none',
      padding: '0.25rem',
      cursor: 'pointer',
      color: '#3b82f6',
      display: 'flex',
      alignItems: 'center'
    }
  };

  const getDayCircleStyle = (date) => {
    const completed = isDateCompleted(date);
    const isToday = date === TODAY;
    const isSelected = date === selectedDate;
    return {
      width: 28,
      height: 28,
      borderRadius: '50%',
      border: `2px solid ${completed || isToday || isSelected ? '#111827' : '#c9d2dc'}`,
      background: completed ? '#111827' : 'transparent',
      color: completed ? '#ffffff' : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      padding: 0,
      boxShadow: isSelected && !completed ? '0 0 0 2px #dfe7ee, 0 0 0 4px #111827' : 'none'
    };
  };

  const getTaskCheckboxStyle = (completed) => ({
    width: 20,
    height: 20,
    minWidth: 20,
    borderRadius: 6,
    border: `2px solid ${completed ? '#111827' : '#c9d2dc'}`,
    background: completed ? '#111827' : 'transparent',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    marginTop: 2
  });

  if (loading) {
    return (
      <div style={styles.outer}>
        <div style={styles.sessionNotes}>Loading today's plan…</div>
      </div>
    );
  }

  const videoUrl = getVideoUrl(videoTask);
  const videoSource = videoUrl ? getVideoSource(videoUrl) : null;

  return (
    <div style={styles.outer}>
      {error && (
        <div style={{ ...styles.sessionNotes, color: '#b91c1c' }}>
          {error}{' '}
          <button type="button" style={{ ...styles.todayLabel, fontSize: '0.8rem' }} onClick={fetchSessions}>
            Retry
          </button>
        </div>
      )}

      {/* Header */}
      <div style={styles.headerRow}>
        <button type="button" style={styles.todayLabel} onClick={resetToToday}>
          {formatDayLabel(selectedDate)}
        </button>
        <div style={styles.streak}>
          <span>{streakCount}</span>
          <i className="pi pi-bolt" style={styles.streakIcon}></i>
        </div>
      </div>

      {/* Week strip with previous / next week buttons */}
      <div style={styles.weekRow}>
        <button type="button" style={styles.navButton} aria-label="Previous week" onClick={() => shiftWeek(-1)}>
          <i className="pi pi-chevron-left"></i>
        </button>

        <div style={{ ...styles.weekStrip, flex: 1 }}>
          {weekDates.map((date, index) => (
            <div key={date} style={styles.dayCol}>
              <span style={{ ...styles.dayLetter, ...(date === TODAY ? styles.dayLetterToday : {}) }}>{WEEK_LABELS[index]}</span>
              <button type="button" style={getDayCircleStyle(date)} aria-label={date} onClick={() => setSelectedDate(date)}>
                {isDateCompleted(date) && <i className="pi pi-check" style={{ fontSize: '0.75rem' }}></i>}
              </button>
            </div>
          ))}
        </div>

        <button type="button" style={styles.navButton} aria-label="Next week" onClick={() => shiftWeek(1)}>
          <i className="pi pi-chevron-right"></i>
        </button>
      </div>

      {/* Session type tabs — shown for any day with at least one session */}
      {sessionsForSelectedDate.length > 0 && (
        <div style={styles.tabRow}>
          {sessionsForSelectedDate.map((session, index) => {
            const color = TYPE_COLORS[session.type] || '#6b7280';
            const active = index === activeSessionIndex;
            return (
              <button key={session._id} type="button" style={styles.tabButton(active, color)} onClick={() => setActiveSessionIndex(index)}>
                {!active && <span style={styles.tabDot(color)}></span>}
                {TYPE_LABELS[session.type] || session.type}
              </button>
            );
          })}
        </div>
      )}

      {/* Selected day's session */}
      <Card className="w-full" style={styles.card}>
        {selectedSession ? (
          <>
            <div style={styles.sessionTitle}>{selectedSession.title}</div>
            <div style={styles.sessionMetaRow}>
              <span style={styles.typeBadge(TYPE_COLORS[selectedSession.type] || '#6b7280')}>{selectedSession.type}</span>
            </div>
            {selectedSession.notes && <div style={styles.sessionNotes}>{selectedSession.notes}</div>}

            <div>
              {selectedSession.tasks
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((task) => {
                  const target = formatTaskTarget(task, selectedSession.type);
                  return (
                    <div key={task._id} style={styles.taskRow}>
                      <button
                        type="button"
                        style={getTaskCheckboxStyle(task.completed)}
                        onClick={() => toggleTask(selectedSession._id, task._id, task.completed)}
                        aria-label={task.name}
                      >
                        {task.completed && <i className="pi pi-check" style={{ fontSize: '0.65rem' }}></i>}
                      </button>
                      <div style={{ flex: 1 }}>
                        <div style={{ ...styles.taskName, ...(task.completed ? styles.taskNameDone : {}) }}>{task.name}</div>
                        {target && <div style={styles.taskTarget}>{target}</div>}
                        {task.description && <div style={styles.taskMeta}>{task.description}</div>}
                      </div>
                      {selectedSession.type === 'gym' && (
                        <button
                          type="button"
                          style={styles.videoButton}
                          aria-label={`Info: ${task.name}`}
                          onClick={() => setVideoTask(task)}
                        >
                          <i className="pi pi-info-circle"></i>
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          </>
        ) : (
          <div style={styles.sessionNotes}>Rest day — no session scheduled.</div>
        )}
      </Card>

      {/* How-to video popup */}
      <Dialog
        header={videoTask?.name}
        visible={!!videoTask}
        onHide={() => setVideoTask(null)}
        style={{ width: '90vw', maxWidth: 560 }}
        dismissableMask
      >
        {videoSource ? (
          videoSource.kind === 'video' ? (
            <video
              src={videoSource.src}
              controls
              playsInline
              style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 8, background: '#000' }}
            />
          ) : videoSource.kind === 'link' ? (
            <a href={videoSource.src} target="_blank" rel="noopener noreferrer" style={styles.videoLink}>
              <i className="pi pi-external-link"></i> Open video in new tab
            </a>
          ) : (
            <iframe
              src={videoSource.src}
              title={videoTask?.name}
              style={{ width: '100%', aspectRatio: '16 / 9', border: 0, borderRadius: 8 }}
              allow="accelerometer; autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          )
        ) : (
          <div style={styles.sessionNotes}>Video coming soon.</div>
        )}

        {videoTask?.instructions && <div style={styles.dialogDescription}>{videoTask.instructions}</div>}
      </Dialog>
    </div>
  );
};

export default DailyTasksWidget;